/**
 * project.types.ts - 项目管理模块类型定义
 *
 * 功能说明：
 * - Project 模型及相关类型定义
 * - 项目状态、预算模式、收入分解等
 *
 * 依赖关系：
 * - 被项目相关页面和组件使用
 * - 依赖 common.types.ts 的基础类型
 *
 * 关键注释索引：
 * - [项目状态] - 见下方 ProjectStatus 枚举
 * - [预算模式] - 见下方 BudgetMode 枚举
 * - [项目接口] - 见下方 Project 接口
 *
 * 创建日期：2026-05-03
 * 作者：AI Assistant
 */

import { ConstructionType } from './common.types';

/**
 * [项目状态] 项目生命周期状态枚举
 *
 * @description 标识项目的当前运行状态
 * - ACTIVE: 进行中
 * - COMPLETED: 已完成
 * - PAUSED: 已暂停
 */
export enum ProjectStatus {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  PAUSED = 'paused'
}

/**
 * [预算模式] 预算计算模式枚举
 *
 * @description 决定预算的计算方式
 * - AUTO: 自动计算（总预算 = 各细分之和）
 * - MANUAL: 手动设置（允许总预算与细分不一致）
 */
export enum BudgetMode {
  AUTO = 'auto',
  MANUAL = 'manual'
}

/**
 * [预算成分] 预算各组成部分接口
 *
 * @description 定义预算的细分类目
 * - labor: 人工预算
 * - equipment: 机械预算
 * - material: 材料预算
 * - subcontract: 分包预算
 * - coordination: 协调预算
 * - contingency: 应急储备
 */
export interface BudgetBreakdown {
  labor: number;
  equipment: number;
  material: number;
  subcontract: number;
  coordination: number;
  contingency: number;
}

/**
 * [收入成分] 收入各组成部分接口
 *
 * @description 定义收入的来源分类
 * - contract: 合同收入
 * - changeOrder: 变更收入
 * - claim: 索赔收入
 * - settlement: 结算收入
 * - other: 其他收入
 */
export interface RevenueBreakdown {
  contract: number;
  changeOrder: number;
  claim: number;
  settlement: number;
  other: number;
}

/**
 * [预算调整类型] 预算变更类型枚举
 *
 * @description 标识预算调整的原因类型
 * - INITIAL: 初始设置
 * - INCREASE: 增加预算
 * - DECREASE: 减少预算
 * - REALLOCATION: 重新分配
 * - CORRECTION: 修正错误
 */
export enum BudgetAdjustmentType {
  INITIAL = 'initial',
  INCREASE = 'increase',
  DECREASE = 'decrease',
  REALLOCATION = 'reallocation',
  CORRECTION = 'correction'
}

/**
 * [预算变化明细] 预算变更的金额明细
 *
 * @description 记录各预算科目变更的金额
 */
export interface BudgetChanges {
  labor: number;
  equipment: number;
  material: number;
  coordination: number;
  contingency: number;
  subcontract: number;
  total: number;
}

/**
 * [预算调整记录] 单次预算调整的完整信息
 *
 * @description 包含调整前后对比、审批信息等
 */
export interface BudgetAdjustment {
  _id: string;
  projectId: string;
  adjustmentType: BudgetAdjustmentType;
  reason: string;
  description?: string;
  previousBreakdown: BudgetBreakdown;
  previousTotal: number;
  newBreakdown: BudgetBreakdown;
  newTotal: number;
  changes: BudgetChanges;
  adjustedBy: {
    _id: string;
    username: string;
    realName?: string;
  };
  adjustedAt: string;
  approvedBy?: {
    _id: string;
    username: string;
    realName?: string;
  };
  approvedAt?: string;
  approvalStatus?: 'pending' | 'approved' | 'rejected';
  approvalComment?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * [预算详情] 项目的完整预算信息
 *
 * @description 包含预算值、收入、版本、锁定状态等
 */
export interface BudgetDetail {
  projectId: string;
  projectName: string;
  budget: number;
  budgetBreakdown: BudgetBreakdown;
  calculatedTotal: number;
  difference: number;
  revenueTotal?: number;
  revenueBreakdown?: RevenueBreakdown;
  calculatedRevenueTotal?: number;
  revenueDifference?: number;
  budgetVersion?: number;
  budgetVersionCount?: number;
  latestVersionReason?: string;
  budgetMode: BudgetMode;
  budgetLocked: boolean;
  budgetLockedAt?: string;
  budgetLockedBy?: {
    _id: string;
    username: string;
    realName?: string;
  };
  /** 实际总成本 = 工时 + 设备 + 材料 + 分包 + 协调 */
  actualCost?: number;
  /** 各模块实际成本明细 */
  costBreakdown?: {
    workTimeCost: number;
    equipmentCost: number;
    materialCost: number;
    subcontractCost: number;
    coordinationCost: number;
  };
}

/**
 * [预算调整统计] 预算调整的汇总统计
 *
 * @description 按类型汇总预算调整次数和金额
 */
export interface BudgetAdjustmentStats {
  byType: Array<{
    _id: BudgetAdjustmentType;
    count: number;
    totalChange: number;
  }>;
  totalAdjustments: number;
  netChange: number;
  firstAdjustment?: string;
  lastAdjustment?: string;
  currentVersion?: number;
  versionCount?: number;
  revenueTotal?: number;
}

/**
 * [预算版本快照] 单次预算版本记录
 *
 * @description 记录历史版本的预算快照
 */
export interface BudgetVersionSnapshot {
  version: number;
  budget: number;
  budgetBreakdown: BudgetBreakdown;
  revenueTotal: number;
  revenueBreakdown: RevenueBreakdown;
  reason?: string;
  createdAt: string;
  createdBy?: string;
}

/**
 * [项目接口] 项目主数据模型
 *
 * @description 包含项目的完整信息
 * - 基本信息：名称、编码、状态、日期
 * - 预算信息：预算总额、各分类预算
 * - 收入信息：收入总额、各来源收入
 * - 版本管理：预算版本号、历史快照
 * - 锁定状态：预算锁定状态和锁定人
 */
export interface Project {
  _id: string;
  name: string;
  projectCode?: string;
  description?: string;
  startDate: string;
  endDate?: string;
  status: ProjectStatus;
  budget?: number;
  budgetBreakdown?: BudgetBreakdown;
  revenueTotal?: number;
  revenueBreakdown?: RevenueBreakdown;
  budgetVersion?: number;
  budgetVersions?: BudgetVersionSnapshot[];
  budgetMode?: BudgetMode;
  budgetLocked?: boolean;
  budgetLockedAt?: string;
  budgetLockedBy?: {
    _id: string;
    username: string;
    realName?: string;
  };
  progress?: number;
  actualCost?: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * [项目查询参数] 查询项目列表的筛选条件
 *
 * @description 用于项目列表和下拉搜索
 */
export interface ProjectQueryParams {
  status?: ProjectStatus;
  keyword?: string;
  page?: number;
  limit?: number;
}
